import { FC } from "react";
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { EditRounded, VisibilityRounded } from "@mui/icons-material";
import { AdminToggle } from "../../../components/ui";
import { ProductsWithFilterNPaginationResponse } from "../../../services/productService";

interface Column {
    id: "name_Product" | "description_Product" | "mechanismAction_Product" | "status" | "actions";
    label: string;
    minWidth?: number;
    align?: "right";
    format?: (value: number) => string;
}

const columns: readonly Column[] = [
    { id: "name_Product", label: "Nome", minWidth: 170 },
    { id: "description_Product", label: "Descrição", minWidth: 170 },
    { id: "mechanismAction_Product", label: "Mecanismo de ação", minWidth: 170 },
    { id: "status", label: "Status", minWidth: 100 },
    { id: "actions", label: "Ações", minWidth: 160 },
];

type Props = {
    rows: ProductsWithFilterNPaginationResponse[];
    rowsPerPage: number;
    page: number;
    count: number;
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onToggle: (product: ProductsWithFilterNPaginationResponse) => void;
    onEdit: (product: ProductsWithFilterNPaginationResponse) => void;
    onView: (product: ProductsWithFilterNPaginationResponse) => void;
};

/** Tabela de produtos (MUI Table + paginação) — admin. */
const ProductTable: FC<Props> = ({ rows, rowsPerPage, page, count, onPageChange, onRowsPerPageChange, onToggle, onEdit, onView }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                backgroundColor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "18px",
                overflow: "hidden",
            }}
        >
            <TableContainer>
                <Table stickyHeader aria-label="tabela de produtos">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    sx={{
                                        minWidth: column.minWidth,
                                        backgroundColor: theme.palette.brand.surface2,
                                        color: "text.secondary",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.06em",
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        py: 1.5,
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                            return (
                                <TableRow
                                    hover
                                    role="checkbox"
                                    tabIndex={-1}
                                    key={row.id_Product}
                                    sx={{
                                        "&:hover": { backgroundColor: theme.palette.brand.surfaceHover },
                                        "& td": { borderBottom: `1px solid ${theme.palette.divider}` },
                                    }}
                                >
                                    {columns.map((column) => {
                                        if (column.id === "status") {
                                            return (
                                                <TableCell key="status">
                                                    <AdminToggle checked={row.disabled_Product !== 1} onChange={() => onToggle(row)} />
                                                </TableCell>
                                            );
                                        }

                                        if (column.id === "actions") {
                                            return (
                                                <TableCell key={row.id_Product + new Date().getTime()} align={columns[0].align}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        <Tooltip title="Editar" placement="top">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => onEdit(row)}
                                                                aria-label="Editar"
                                                                sx={{ width: 34, height: 34, borderRadius: "9px" }}
                                                            >
                                                                <EditRounded sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Visualizar" placement="top">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => onView(row)}
                                                                aria-label="Visualizar"
                                                                sx={{ width: 34, height: 34, borderRadius: "9px" }}
                                                            >
                                                                <VisibilityRounded sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            );
                                        }

                                        const value = row[column.id as keyof typeof row];
                                        return (
                                            <TableCell key={column.id} align={column.align}>
                                                <Typography sx={{ fontSize: "14px", color: "text.primary" }}>{value as string}</Typography>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />
        </Box>
    );
};

export default ProductTable;
